import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../utils/apiUtils';
import styled, { keyframes, css } from 'styled-components';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const PaymentPageWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  padding: 20px;
`;

const PaymentContainer = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  padding: 20px 28px;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 0, 0, 0.05);
  text-align: center;
  max-width: 900px;
  width: 100%;
  animation: ${fadeIn} 0.4s ease-out;
`;

const CompanyLogo = styled.img`
  max-width: 100%;
  height: auto;
  margin-bottom: 16px;
  display: block;
  margin-left: auto;
  margin-right: auto;
`;

const Header = styled.h1`
  color: #1a2b4d;
  font-size: 1.5rem;
  margin-bottom: 4px;
  font-weight: 700;
`;

const SubHeader = styled.p`
  color: #64748b;
  font-size: 0.9rem;
  margin-bottom: 16px;
`;

const InfoText = styled.p`
  color: #555;
  font-size: 1rem;
  line-height: 1.5;
  margin-bottom: 24px;
`;

const PaymentOptionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
  margin-bottom: 16px;
  
  @media (max-width: 800px) {
    grid-template-columns: 1fr 1fr;
  }
  
  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const PaymentCard = styled.div<{ selected?: boolean; disabled?: boolean }>`
  background: ${props => props.selected ? 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)' : '#fafafa'};
  border: 2px solid ${props => props.selected ? '#0ea5e9' : '#e5e7eb'};
  border-radius: 12px;
  padding: 14px 16px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  opacity: ${props => props.disabled ? 0.6 : 1};
  display: flex;
  align-items: center;
  gap: 12px;
  text-align: left;
  
  &:hover {
    ${props => !props.disabled && `
      border-color: #0ea5e9;
      transform: translateY(-1px);
      box-shadow: 0 4px 15px rgba(14, 165, 233, 0.15);
    `}
  }
  
  ${props => props.selected && `
    box-shadow: 0 4px 15px rgba(14, 165, 233, 0.2);
  `}
`;

const CardIcon = styled.div<{ color: string }>`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

const CardTitle = styled.h3`
  font-size: 0.95rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 2px;
`;

const CardDesc = styled.p`
  font-size: 0.8rem;
  color: #64748b;
  margin: 0;
`;

const SelectedBadge = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  background: #0ea5e9;
  color: white;
  font-size: 0.65rem;
  font-weight: 600;
  padding: 3px 6px;
  border-radius: 12px;
`;

const Amount = styled.span`
  font-weight: 700;
  color: #000;
`;

const PayNowButton = styled.button`
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 14px 28px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  box-shadow: 0 4px 15px rgba(14, 165, 233, 0.3);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: linear-gradient(135deg, #0284c7 0%, #0369a1 100%);
    box-shadow: 0 6px 20px rgba(14, 165, 233, 0.4);
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 10px rgba(14, 165, 233, 0.2);
  }

  &:disabled {
    background: linear-gradient(135deg, #94a3b8 0%, #64748b 100%);
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
  }
`;

const HorizontalLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  text-align: left;
  
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const DDUploadArea = styled.div<{ hasFile?: boolean; isValidating?: boolean }>`
  border: 2px dashed ${props => props.hasFile ? '#22c55e' : '#cbd5e1'};
  border-radius: 10px;
  padding: 14px;
  background: ${props => props.hasFile ? 'rgba(34, 197, 94, 0.05)' : 'rgba(241, 245, 249, 0.5)'};
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  
  &:hover {
    border-color: ${props => props.hasFile ? '#22c55e' : '#0ea5e9'};
    background: ${props => props.hasFile ? 'rgba(34, 197, 94, 0.08)' : 'rgba(14, 165, 233, 0.05)'};
  }
  
  ${props => props.isValidating && css`
    background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
    background-size: 200% 100%;
    animation: ${shimmer} 1.5s infinite;
  `}
`;

const DDPreview = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  background: white;
  border-radius: 8px;
  margin-top: 10px;
  border: 1px solid #e2e8f0;
`;

const DDDetails = styled.div`
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border: 1px solid #bbf7d0;
  border-radius: 10px;
  padding: 12px;
  text-align: left;
`;

const DDDetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  font-size: 0.8rem;
  border-bottom: 1px solid rgba(34, 197, 94, 0.2);
  
  &:last-child {
    border-bottom: none;
  }
  
  span:first-child {
    color: #64748b;
  }
  
  span:last-child {
    color: #1e293b;
    font-weight: 600;
  }
`;

const ValidationStatus = styled.div<{ status: 'validating' | 'success' | 'error' }>`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  margin-top: 10px;
  font-size: 0.8rem;
  font-weight: 500;
  
  ${props => props.status === 'validating' && `
    background: #fef3c7;
    color: #92400e;
  `}
  
  ${props => props.status === 'success' && `
    background: #dcfce7;
    color: #166534;
  `}
  
  ${props => props.status === 'error' && `
    background: #fee2e2;
    color: #991b1b;
  `}
`;

const RejectionList = styled.ul`
  margin: 4px 0 0 0;
  padding-left: 16px;
  font-size: 0.78rem;
  line-height: 1.5;
  
  li {
    margin-bottom: 2px;
  }
`;

const ImageModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
`;

const ModalControls = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  background: rgba(255, 255, 255, 0.1);
  padding: 8px 12px;
  border-radius: 8px;
`;

const ModalButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const ModalImageContainer = styled.div`
  max-width: 90vw;
  max-height: 70vh;
  overflow: hidden;
  cursor: grab;
  
  &:active {
    cursor: grabbing;
  }
`;

const GoBackButton = styled.button`
  background: transparent;
  color: #64748b;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px 20px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  width: 100%;
  margin-top: 10px;
  transition: all 0.3s ease;

  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
    color: #475569;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

interface DDDetailsData {
  ddNumber: string;
  bankName: string;
  branchName: string;
  amount: string;
  date: string;
  payeeName: string;
  isValid: boolean;
  isDateExpired?: boolean;
}

const PaymentDetailsPage: React.FC = () => {
  const { applicationId } = useParams<{ applicationId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [paymentAmount, setPaymentAmount] = useState<number>(5000);
  
  // Payment method selection
  const [paymentMethod, setPaymentMethod] = useState<'online' | 'dd' | 'netbanking'>('online');
  
  // Demand Draft state
  const [ddFile, setDdFile] = useState<File | null>(null);
  const [ddPreviewUrl, setDdPreviewUrl] = useState<string | null>(null);
  const [ddValidating, setDdValidating] = useState(false);
  const [ddValidated, setDdValidated] = useState(false);
  const [ddError, setDdError] = useState<string | null>(null);
  const [ddDetails, setDdDetails] = useState<DDDetailsData | null>(null);
  const ddFileInputRef = useRef<HTMLInputElement>(null);
  
  // NetBanking state
  const [nbFile, setNbFile] = useState<File | null>(null);
  const [nbPreviewUrl, setNbPreviewUrl] = useState<string | null>(null);
  const [nbError, setNbError] = useState<string | null>(null);
  const nbFileInputRef = useRef<HTMLInputElement>(null);
  const [bankDetailsExpanded, setBankDetailsExpanded] = useState(true);
  
  // Image modal state
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageZoom, setImageZoom] = useState(1);
  const [imageRotation, setImageRotation] = useState(0);
  const [imagePan, setImagePan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID;
  
  // Fetch payment amount from settings and check payment status
  useEffect(() => {
    const fetchPaymentSettings = async () => {
      try {
        const settingsResponse = await api.get('/settings/payment-button-status');
        if (settingsResponse.data.success && settingsResponse.data.paymentAmount) {
          setPaymentAmount(settingsResponse.data.paymentAmount);
        }
      } catch (error) {
        console.error('[PaymentDetailsPage] Error fetching payment settings:', error);
      }
    };
    
    const checkPaymentStatus = async () => {
      try {
        console.log('[PaymentDetailsPage] Checking application status for:', applicationId);
        const response = await api.get(`/applications/${applicationId}`);
        
        if (response.data.success && response.data.application) {
          const status = response.data.application.status;
          console.log('[PaymentDetailsPage] Application status:', status);
          
          if (status === 'payment_complete') {
            console.log('[PaymentDetailsPage] Payment already completed, redirecting to thank-you page');
            navigate(`/thank-you/${applicationId}`, { replace: true });
            return;
          }
        }
      } catch (error) {
        console.error('[PaymentDetailsPage] Error checking payment status:', error);
      } finally {
        setCheckingStatus(false);
      }
    };
    
    fetchPaymentSettings();
    if (applicationId) {
      checkPaymentStatus();
    }
  }, [applicationId, navigate]);

  // Function to load a script dynamically
  const loadScript = (src: string) => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = src;
      script.onload = () => {
        resolve(true);
      };
      script.onerror = () => {
        resolve(false);
      };
      document.body.appendChild(script);
    });
  };

  const handlePayment = async () => {
    setLoading(true);
    setError(null); // Clear previous errors
    if (!RAZORPAY_KEY_ID) {
      alert('Razorpay Key ID is not configured. Please check your environment variables.');
      setLoading(false);
      return;
    }

    const res = await loadScript('https://checkout.razorpay.com/v1/checkout.js');
    if (!res) {
      alert('Razorpay SDK failed to load. Are you online?');
      setLoading(false);
      return;
    }

    try {
      console.log('[PaymentDetailsPage] Starting payment process for application ID:', applicationId);
      
      // 1. Create Order API call using the centralized api instance
      console.log('[PaymentDetailsPage] Creating Razorpay order with amount:', paymentAmount * 100);
      const orderResponse = await api.post('/razorpay/create-order', {
        amount: paymentAmount * 100, // Amount in paise
        currency: 'INR',
        receipt: `receipt_order_${applicationId}`,
        applicationId: applicationId,
      });

      console.log('[PaymentDetailsPage] Raw orderResponse from backend:', JSON.stringify(orderResponse, null, 2));

      const orderData = orderResponse.data;
      console.log('[PaymentDetailsPage] Extracted orderData from response.data:', JSON.stringify(orderData, null, 2));
      console.log('[PaymentDetailsPage] Checking orderData.order.id immediately after extraction:', orderData?.order?.id);

      if (!orderData || !orderData.order || !orderData.order.id) {
        console.error('[PaymentDetailsPage] CRITICAL: orderData.id is missing immediately after create-order call. Order creation failed or response malformed.');
        setError('Failed to create payment order. Missing order ID from server. Please try again or contact support.');
        setLoading(false);
        return;
      }

      console.log('[PaymentDetailsPage] Order created successfully, orderData.order.id is present:', orderData.order.id);

      const options = {
        key: RAZORPAY_KEY_ID,
        amount: orderData.amount, // Amount in paise from backend
        currency: orderData.currency,
        name: 'CNG Dealership Application',
        description: 'Application Fee Payment',
        order_id: orderData.order.id, // Order ID from backend
        handler: async function (razorpayCallbackResponse: any) {
          console.log('[PaymentDetailsPage] Razorpay payment callback received:', razorpayCallbackResponse);

          // Check for errors from Razorpay itself
          if (razorpayCallbackResponse.error) {
            console.error('[PaymentDetailsPage] Razorpay payment failed directly:', razorpayCallbackResponse.error);
            const errorDesc = razorpayCallbackResponse.error.description || 'Unknown Razorpay error';
            const errorReason = razorpayCallbackResponse.error.reason || 'N/A';
            setError(`Payment failed: ${errorDesc}. Reason: ${errorReason}. Please try again or contact support.`);
            setLoading(false);
            return; // Stop further processing
          }

          // Proceed with verification if no direct Razorpay error
          try {
            console.log('[PaymentDetailsPage] Checking payment verification values (post-Razorpay callback):');
            console.log('- orderData (from create-order scope):', orderData); // This is the orderData from create-order call
            console.log('- order_id from orderData (should be orderData.order.id):', orderData?.order?.id);
            console.log('- applicationId:', applicationId);
            console.log('- payment_id from Razorpay callback:', razorpayCallbackResponse.razorpay_payment_id);
            console.log('- signature from Razorpay callback:', razorpayCallbackResponse.razorpay_signature);

            // Critical data checks
            if (!orderData || !orderData.order || !orderData.order.id) {
              console.error('[PaymentDetailsPage] CRITICAL ERROR: orderData.id is missing before sending for verification.');
              setError('Payment verification failed: Missing internal order reference. Please contact support.');
              setLoading(false);
              return;
            }
            if (!razorpayCallbackResponse.razorpay_payment_id || !razorpayCallbackResponse.razorpay_signature) {
              console.error('[PaymentDetailsPage] CRITICAL ERROR: Razorpay payment_id or signature missing from callback.');
              setError('Payment verification failed: Incomplete payment details from gateway. Please contact support.');
              setLoading(false);
              return;
            }

            const verificationPayload = {
              razorpay_order_id: orderData.order.id, // This MUST be the order_id from our backend's /create-order
              razorpay_payment_id: razorpayCallbackResponse.razorpay_payment_id,
              razorpay_signature: razorpayCallbackResponse.razorpay_signature,
              applicationId: applicationId
            };
            
            console.log('[PaymentDetailsPage] Sending verification payload:', verificationPayload);
            
            const verifyFetchResponse = await api.post('/razorpay/verify-payment', verificationPayload);

            console.log('[PaymentDetailsPage] Verification response status:', verifyFetchResponse.status);
            const verificationAPIResponse = verifyFetchResponse.data;
            console.log('[PaymentDetailsPage] Verification response data:', verificationAPIResponse);

            if (verificationAPIResponse.success) {
              console.log('[PaymentDetailsPage] Payment verification successful, navigating to thank you page');
              navigate(`/thank-you/${applicationId}`);
            } else {
              throw new Error(verificationAPIResponse.message || 'Payment verification failed as per backend.');
            }

          } catch (verifyError: any) {
            console.error('[PaymentDetailsPage] Payment verification processing error:', verifyError);
            setError(verifyError.message || 'An unexpected error occurred during payment verification. Please contact support.');
            setLoading(false);
          }
        },
        prefill: {
          // You can prefill customer details here if available
          // name: 'User Name',
          // email: 'user@example.com',
          // contact: '9999999999',
        },
        notes: {
          application_id: applicationId,
        },
        theme: {
          color: '#007bff',
        },
      };

      // @ts-ignore
      const paymentObject = new window.Razorpay(options);
      paymentObject.on('payment.failed', function (response: any) {
        console.error('Payment failed:', response.error);
        alert(
          `Payment Failed: ${response.error.description}. Code: ${response.error.code}. Source: ${response.error.source}. Step: ${response.error.step}. Reason: ${response.error.reason}. Order ID: ${response.error.metadata.order_id}. Payment ID: ${response.error.metadata.payment_id}`
        );
      });
      paymentObject.open();

    } catch (error: any) {
      console.error('Payment processing error:', error);
      const errorMessage = error.response?.data?.message || error.message;
      alert(`Error: ${errorMessage}`);
    }
    setLoading(false);
  };

  // DD file upload handler
  const handleDDFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setDdError('Please upload an image file (JPG, PNG)');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setDdError('File size must be less than 5MB');
      return;
    }
    
    setDdFile(file);
    setDdError(null);
    setDdValidated(false);
    setDdDetails(null);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setDdPreviewUrl(e.target?.result as string);
    };
    reader.readAsDataURL(file);
    
    // Auto-validate with AI
    await validateDDWithAI(file);
  };
  
  // AI validation for DD
  const validateDDWithAI = async (file: File) => {
    setDdValidating(true);
    setDdError(null);
    
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const base64Data = e.target?.result as string;
        
        const response = await api.post('/validate-demand-draft', {
          imageData: base64Data,
          expectedAmount: paymentAmount
        });
        
        console.log('[DD Validate] Response:', response.data);
        
        if (response.data.success) {
          setDdDetails(response.data.details);
          setDdValidated(response.data.details.isValid);
          if (!response.data.details.isValid) {
            // Use rejection reasons if available, otherwise use message
            const reasons = response.data.rejectionReasons?.length > 0 
              ? response.data.rejectionReasons.join('\n') 
              : response.data.message;
            setDdError(reasons || 'DD validation failed. Please check the details.');
          }
        } else {
          setDdError(response.data.message || 'Failed to validate DD. Please try again.');
          setDdValidated(false);
        }
      } catch (err: any) {
        console.error('DD validation error:', err);
        setDdError(err.response?.data?.message || 'Failed to validate DD. Please try again.');
        setDdValidated(false);
      }
      setDdValidating(false);
    };
    reader.onerror = () => {
      setDdError('Failed to read the file. Please try again.');
      setDdValidating(false);
    };
    reader.readAsDataURL(file);
  };
  
  // Common helper to submit DD payment (auto or manual review)
  const submitDDPayment = async (mode: 'auto' | 'manual') => {
    if (!ddFile || !ddDetails) {
      setDdError('Please upload a DD first');
      return;
    }

    if (mode === 'auto' && !ddValidated) {
      setDdError('DD must pass validation before automatic submission. Use Submit anyway for manual review.');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('ddImage', ddFile);
      formData.append('applicationId', applicationId || '');
      formData.append('ddDetails', JSON.stringify(ddDetails));
      formData.append('reviewMode', mode);
      
      const response = await api.post('/submit-dd-payment', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        navigate(`/thank-you/${applicationId}?method=dd`);
      } else {
        setError(response.data.message || 'Failed to submit DD payment');
      }
    } catch (err: any) {
      console.error('DD submission error:', err);
      setError(err.response?.data?.message || 'Failed to submit DD payment');
    }
    setLoading(false);
  };

  // Submit DD payment (automatic path, only if validated)
  const handleDDSubmit = async () => {
    await submitDDPayment('auto');
  };

  // Submit DD for manual review even if validation failed
  const handleDDManualSubmit = async () => {
    await submitDDPayment('manual');
  };
  
  // Clear DD
  const clearDD = () => {
    setDdFile(null);
    setDdPreviewUrl(null);
    setDdValidated(false);
    setDdDetails(null);
    setDdError(null);
    if (ddFileInputRef.current) {
      ddFileInputRef.current.value = '';
    }
  };
  
  // NetBanking file upload handler
  const handleNBFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setNbError('Please upload an image file (JPG, PNG)');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setNbError('File size must be less than 5MB');
      return;
    }
    
    setNbFile(file);
    setNbError(null);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setNbPreviewUrl(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };
  
  // Submit NetBanking payment
  const handleNBSubmit = async () => {
    if (!nbFile) {
      setNbError('Please upload a NetBanking payment screenshot');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('nbImage', nbFile);
      formData.append('applicationId', applicationId || '');
      
      const response = await api.post('/submit-netbanking-payment', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        navigate(`/thank-you/${applicationId}?method=netbanking`);
      } else {
        setError(response.data.message || 'Failed to submit NetBanking payment');
      }
    } catch (err: any) {
      console.error('NetBanking submission error:', err);
      setError(err.response?.data?.message || 'Failed to submit NetBanking payment');
    }
    setLoading(false);
  };
  
  // Clear NetBanking
  const clearNB = () => {
    setNbFile(null);
    setNbPreviewUrl(null);
    setNbError(null);
    if (nbFileInputRef.current) {
      nbFileInputRef.current.value = '';
    }
  };
  
  // Image modal handlers
  const openImageModal = () => {
    setShowImageModal(true);
    setImageZoom(1);
    setImageRotation(0);
    setImagePan({ x: 0, y: 0 });
  };
  
  const closeImageModal = () => {
    setShowImageModal(false);
  };
  
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - imagePan.x, y: e.clientY - imagePan.y });
  };
  
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    setImagePan({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };
  
  const handleMouseUp = () => {
    setIsDragging(false);
  };
  
  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setImageZoom(prev => Math.max(0.5, Math.min(5, prev + delta)));
  };
  
  // Parse rejection reasons from error message
  const parseRejectionReasons = (errorMsg: string): string[] => {
    if (!errorMsg) return [];
    // Check if it contains common separators
    if (errorMsg.includes('•')) return errorMsg.split('•').filter(s => s.trim());
    if (errorMsg.includes('\n')) return errorMsg.split('\n').filter(s => s.trim());
    if (errorMsg.includes(';')) return errorMsg.split(';').filter(s => s.trim());
    // Return as single item if no separators
    return [errorMsg];
  };

  if (checkingStatus) {
    return (
      <PaymentPageWrapper>
        <PaymentContainer>
          <CompanyLogo src="/assets/images/banner.png" alt="Company Banner" />
          <Header>Checking Payment Status...</Header>
          <InfoText>Please wait while we verify your application status.</InfoText>
        </PaymentContainer>
      </PaymentPageWrapper>
    );
  }
  
  return (
    <PaymentPageWrapper>
      <PaymentContainer>
        <CompanyLogo src="/assets/images/banner.png" alt="Company Banner" />
        <Header>Application Fee Payment</Header>
        <SubHeader>
          Pay <Amount>₹{paymentAmount.toLocaleString('en-IN')}</Amount> (non-refundable) to complete your application
        </SubHeader>
        
        {error && <p style={{ color: '#dc2626', marginBottom: '16px', fontSize: '0.9rem', background: '#fef2f2', padding: '10px 14px', borderRadius: '8px' }}>⚠️ {error}</p>}
        
        {/* Payment Method Selection */}
        <PaymentOptionsGrid>
          {/* Online Payment Card */}
          <PaymentCard 
            selected={paymentMethod === 'online'} 
            onClick={() => setPaymentMethod('online')}
          >
            {paymentMethod === 'online' && <SelectedBadge>Selected</SelectedBadge>}
            <CardIcon color="linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)">💳</CardIcon>
            <CardTitle>Pay Online</CardTitle>
            <CardDesc>Instant payment via UPI, Cards, Net Banking</CardDesc>
          </PaymentCard>
          
          {/* Demand Draft Card */}
          <PaymentCard 
            selected={paymentMethod === 'dd'} 
            onClick={() => setPaymentMethod('dd')}
          >
            {paymentMethod === 'dd' && <SelectedBadge>Selected</SelectedBadge>}
            <CardIcon color="linear-gradient(135deg, #10b981 0%, #059669 100%)">📄</CardIcon>
            <CardTitle>Demand Draft</CardTitle>
            <CardDesc>Upload DD image for verification</CardDesc>
          </PaymentCard>
          
          {/* NetBanking Card */}
          <PaymentCard 
            selected={paymentMethod === 'netbanking'} 
            onClick={() => setPaymentMethod('netbanking')}
          >
            {paymentMethod === 'netbanking' && <SelectedBadge>Selected</SelectedBadge>}
            <CardIcon color="linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)">🏦</CardIcon>
            <CardTitle>NetBanking</CardTitle>
            <CardDesc>Upload payment screenshot</CardDesc>
          </PaymentCard>
        </PaymentOptionsGrid>
        
        {/* Online Payment Section */}
        {paymentMethod === 'online' && (
          <div style={{ animation: 'fadeIn 0.3s ease-out' }}>
            <PayNowButton onClick={handlePayment} disabled={loading}>
              {loading ? '⏳ Processing...' : '💳 Pay Online'}
            </PayNowButton>
          </div>
        )}
        
        {/* Demand Draft Section */}
        {paymentMethod === 'dd' && (
          <div style={{ animation: 'fadeIn 0.3s ease-out' }}>
            <input
              type="file"
              ref={ddFileInputRef}
              accept="image/jpeg,image/jpg,image/png"
              onChange={handleDDFileChange}
              style={{ display: 'none' }}
            />
            
            <HorizontalLayout>
              {/* Left: Upload Area & Preview */}
              <div>
                <DDUploadArea 
                  hasFile={!!ddFile} 
                  isValidating={ddValidating}
                  onClick={() => !ddValidating && ddFileInputRef.current?.click()}
                >
                  {!ddFile ? (
                    <>
                      <div style={{ fontSize: '1.8rem', marginBottom: '6px' }}>📤</div>
                      <div style={{ fontWeight: 600, color: '#334155', fontSize: '0.85rem' }}>
                        Upload DD Image
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                        JPG, PNG up to 5MB
                      </div>
                    </>
                  ) : ddValidating ? (
                    <>
                      <div style={{ fontSize: '1.5rem', marginBottom: '6px' }}>🔍</div>
                      <div style={{ fontWeight: 600, color: '#92400e', fontSize: '0.85rem' }}>
                        Validating...
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize: '1.5rem', marginBottom: '6px' }}>{ddValidated ? '✅' : '📄'}</div>
                      <div style={{ fontWeight: 600, color: ddValidated ? '#166534' : '#334155', fontSize: '0.85rem' }}>
                        {ddValidated ? 'Validated' : 'Uploaded'}
                      </div>
                      <div style={{ fontSize: '0.7rem', color: '#64748b' }}>
                        Click to change
                      </div>
                    </>
                  )}
                </DDUploadArea>
                
                {/* DD Preview with clickable thumbnail */}
                {ddPreviewUrl && !ddValidating && (
                  <DDPreview>
                    <img 
                      src={ddPreviewUrl} 
                      alt="DD Preview" 
                      onClick={openImageModal}
                      style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '6px', cursor: 'pointer' }}
                      title="Click to zoom"
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.8rem', color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '120px' }}>
                        {ddFile?.name}
                      </div>
                      <div style={{ fontSize: '0.7rem', color: '#64748b' }}>
                        {ddFile && (ddFile.size / 1024).toFixed(1)} KB
                      </div>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); clearDD(); }}
                      style={{ background: '#fee2e2', border: 'none', borderRadius: '4px', padding: '4px 8px', color: '#dc2626', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 500 }}
                    >
                      ✕
                    </button>
                  </DDPreview>
                )}
                
                {/* Validation Status */}
                {ddValidating && (
                  <ValidationStatus status="validating">
                    ⏳ Analyzing...
                  </ValidationStatus>
                )}
                
                {ddError && (
                  <ValidationStatus status="error">
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: '4px' }}>❌ Validation Failed</div>
                      <RejectionList>
                        {parseRejectionReasons(ddError).map((reason, idx) => (
                          <li key={idx}>{reason}</li>
                        ))}
                      </RejectionList>
                    </div>
                  </ValidationStatus>
                )}
                
                {ddValidated && ddDetails && (
                  <ValidationStatus status="success">
                    ✅ Verified
                  </ValidationStatus>
                )}
              </div>
              
              {/* Right: DD Details */}
              <div>
                {ddDetails ? (
                  <DDDetails style={ddDetails.isDateExpired ? { background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)', borderColor: '#fca5a5' } : {}}>
                    <div style={{ fontWeight: 600, marginBottom: '8px', color: ddDetails.isDateExpired ? '#dc2626' : '#166534', fontSize: '0.85rem' }}>
                      {ddDetails.isDateExpired ? '⚠️ DD Details (Expired)' : '📋 DD Details'}
                    </div>
                    <DDDetailRow>
                      <span>DD No.</span>
                      <span>{ddDetails.ddNumber}</span>
                    </DDDetailRow>
                    <DDDetailRow>
                      <span>Bank</span>
                      <span>{ddDetails.bankName}</span>
                    </DDDetailRow>
                    <DDDetailRow>
                      <span>Branch</span>
                      <span>{ddDetails.branchName}</span>
                    </DDDetailRow>
                    <DDDetailRow>
                      <span>Amount</span>
                      <span>₹{ddDetails.amount}</span>
                    </DDDetailRow>
                    <DDDetailRow style={ddDetails.isDateExpired ? { background: '#fee2e2', borderRadius: '4px', padding: '4px 6px', margin: '-4px -6px' } : {}}>
                      <span style={ddDetails.isDateExpired ? { color: '#dc2626', fontWeight: 600 } : {}}>Date</span>
                      <span style={ddDetails.isDateExpired ? { color: '#dc2626', fontWeight: 600 } : {}}>{ddDetails.date} {ddDetails.isDateExpired && '❌'}</span>
                    </DDDetailRow>
                    {ddDetails.isDateExpired && (
                      <div style={{ background: '#dc2626', color: 'white', padding: '6px 8px', borderRadius: '4px', fontSize: '0.75rem', marginTop: '8px', textAlign: 'center' }}>
                        DD must be dated within 2 weeks
                      </div>
                    )}
                    <DDDetailRow>
                      <span>Payee</span>
                      <span>{ddDetails.payeeName}</span>
                    </DDDetailRow>
                  </DDDetails>
                ) : (
                  <div style={{ background: '#f8fafc', borderRadius: '10px', padding: '16px', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '0.85rem', textAlign: 'center' }}>
                    {ddValidating ? 'Extracting DD details...' : 'Upload a DD to see extracted details'}
                  </div>
                )}
              </div>
            </HorizontalLayout>
            
            {/* Primary DD submit (only when validated and not expired) */}
            <PayNowButton 
              onClick={handleDDSubmit} 
              disabled={loading || !ddValidated || (ddDetails?.isDateExpired ?? false)}
              style={{ marginTop: '12px', marginRight: '8px' }}
            >
              {loading
                ? '⏳ Submitting...'
                : ddValidated && !(ddDetails?.isDateExpired)
                  ? '✓ Submit DD Payment'
                  : '📤 Upload & Validate DD First'}
            </PayNowButton>

            {/* Manual review submit - visible when DD exists but is not valid/expired */}
            {ddFile && ddDetails && (!ddValidated || ddDetails.isDateExpired) && (
              <PayNowButton
                onClick={handleDDManualSubmit}
                disabled={loading}
                style={{
                  marginTop: '12px',
                  backgroundColor: '#f97316',
                  borderColor: '#ea580c'
                }}
              >
                {loading ? '⏳ Submitting for review...' : '⚠ Submit anyway (Manual review)'}
              </PayNowButton>
            )}
          </div>
        )}
        
        {/* NetBanking Section */}
        {paymentMethod === 'netbanking' && (
          <div style={{ animation: 'fadeIn 0.3s ease-out' }}>
            {/* Bank Details Card - Collapsible */}
            <div style={{
              background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '12px',
              color: 'white',
              boxShadow: '0 2px 8px rgba(30, 58, 95, 0.25)'
            }}>
              <div 
                onClick={() => setBankDetailsExpanded(!bankDetailsExpanded)}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none' }}
              >
                <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: '6px', padding: '5px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 14v3M12 14v3M16 14v3"/>
                  </svg>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>Bank Account Details</div>
                  <div style={{ fontSize: '0.65rem', opacity: 0.7 }}>Click to {bankDetailsExpanded ? 'collapse' : 'expand'}</div>
                </div>
                <div style={{ background: '#fbbf24', color: '#1e3a5f', borderRadius: '4px', padding: '4px 8px', fontSize: '0.7rem', fontWeight: 600 }}>
                  ₹{paymentAmount.toLocaleString()}
                </div>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ transform: bankDetailsExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>
                  <path d="M6 9l6 6 6-6"/>
                </svg>
              </div>
              
              {bankDetailsExpanded && (
                <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.15)' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', fontSize: '0.72rem' }}>
                    <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: '5px', padding: '6px 8px' }}>
                      <div style={{ opacity: 0.6, fontSize: '0.6rem' }}>Bank</div>
                      <div style={{ fontWeight: 600 }}>Axis Bank Ltd</div>
                    </div>
                    <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: '5px', padding: '6px 8px' }}>
                      <div style={{ opacity: 0.6, fontSize: '0.6rem' }}>Branch</div>
                      <div style={{ fontWeight: 600 }}>Senapati Bapat Marg</div>
                    </div>
                    <div style={{ background: 'rgba(255,255,255,0.12)', borderRadius: '5px', padding: '6px 8px', gridColumn: 'span 2' }}>
                      <div style={{ opacity: 0.6, fontSize: '0.6rem' }}>Account Number</div>
                      <div style={{ fontWeight: 700, fontSize: '0.9rem', letterSpacing: '0.5px', fontFamily: 'monospace' }}>315010200006385</div>
                    </div>
                    <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: '5px', padding: '6px 8px' }}>
                      <div style={{ opacity: 0.6, fontSize: '0.6rem' }}>IFSC Code</div>
                      <div style={{ fontWeight: 600, fontFamily: 'monospace' }}>UTIB0000315</div>
                    </div>
                    <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: '5px', padding: '6px 8px' }}>
                      <div style={{ opacity: 0.6, fontSize: '0.6rem' }}>Account Type</div>
                      <div style={{ fontWeight: 600 }}>Current Account</div>
                    </div>
                    <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: '5px', padding: '6px 8px' }}>
                      <div style={{ opacity: 0.6, fontSize: '0.6rem' }}>MICR</div>
                      <div style={{ fontWeight: 600, fontFamily: 'monospace' }}>411211009</div>
                    </div>
                    <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: '5px', padding: '6px 8px' }}>
                      <div style={{ opacity: 0.6, fontSize: '0.6rem' }}>Swift</div>
                      <div style={{ fontWeight: 600, fontFamily: 'monospace' }}>AXISINBB315</div>
                    </div>
                  </div>
                  <div style={{ marginTop: '6px', background: 'rgba(255,255,255,0.06)', borderRadius: '5px', padding: '6px 8px', fontSize: '0.62rem', opacity: 0.8 }}>
                    <span style={{ opacity: 0.6 }}>Address: </span>Lohia & Jain Bldg, Ambika Society, Plot 20, S.B.Road, Pune - 411016
                  </div>
                </div>
              )}
            </div>

            <input
              type="file"
              ref={nbFileInputRef}
              accept="image/jpeg,image/jpg,image/png"
              onChange={handleNBFileChange}
              style={{ display: 'none' }}
            />
            
            <DDUploadArea 
              hasFile={!!nbFile}
              onClick={() => nbFileInputRef.current?.click()}
              style={{ marginBottom: '16px' }}
            >
              {!nbFile ? (
                <>
                  <div style={{ fontSize: '1.8rem', marginBottom: '6px' }}>🏦</div>
                  <div style={{ fontWeight: 600, color: '#334155', fontSize: '0.85rem' }}>
                    Upload NetBanking Payment Screenshot
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                    JPG, PNG up to 5MB
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: '1.5rem', marginBottom: '6px' }}>✅</div>
                  <div style={{ fontWeight: 600, color: '#166534', fontSize: '0.85rem' }}>
                    Screenshot Uploaded
                  </div>
                  <div style={{ fontSize: '0.7rem', color: '#64748b' }}>
                    Click to change
                  </div>
                </>
              )}
            </DDUploadArea>
            
            {/* NetBanking Preview */}
            {nbPreviewUrl && (
              <DDPreview>
                <img 
                  src={nbPreviewUrl} 
                  alt="NetBanking Screenshot" 
                  onClick={openImageModal}
                  style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '6px', cursor: 'pointer' }}
                  title="Click to zoom"
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.8rem', color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '200px' }}>
                    {nbFile?.name}
                  </div>
                  <div style={{ fontSize: '0.7rem', color: '#64748b' }}>
                    {nbFile && (nbFile.size / 1024).toFixed(1)} KB
                  </div>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); clearNB(); }}
                  style={{ background: '#fee2e2', border: 'none', borderRadius: '4px', padding: '4px 8px', color: '#dc2626', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 500 }}
                >
                  ✕
                </button>
              </DDPreview>
            )}
            
            {nbError && (
              <ValidationStatus status="error">
                ❌ {nbError}
              </ValidationStatus>
            )}
            
            <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px', padding: '12px', marginTop: '12px', fontSize: '0.8rem', color: '#0369a1' }}>
              <strong>Note:</strong> Upload a screenshot of your completed NetBanking payment. Your application will be submitted for manual verification.
            </div>
            
            <PayNowButton 
              onClick={handleNBSubmit} 
              disabled={loading || !nbFile}
              style={{ marginTop: '16px' }}
            >
              {loading ? '⏳ Submitting...' : nbFile ? '📤 Submit Payment Screenshot' : '📤 Upload Screenshot First'}
            </PayNowButton>
          </div>
        )}
        
        {/* Image Preview Modal */}
        {showImageModal && (ddPreviewUrl || nbPreviewUrl) && (
          <ImageModal onClick={closeImageModal}>
            <ModalControls onClick={e => e.stopPropagation()}>
              <ModalButton onClick={() => setImageZoom(z => Math.min(5, z + 0.5))}>🔍+</ModalButton>
              <ModalButton onClick={() => setImageZoom(z => Math.max(0.5, z - 0.5))}>🔍-</ModalButton>
              <ModalButton onClick={() => setImageRotation(r => r - 15)}>↶ 15°</ModalButton>
              <ModalButton onClick={() => setImageRotation(r => r + 15)}>↷ 15°</ModalButton>
              <ModalButton onClick={() => { setImageZoom(1); setImageRotation(0); setImagePan({ x: 0, y: 0 }); }}>Reset</ModalButton>
              <ModalButton onClick={closeImageModal}>✕ Close</ModalButton>
            </ModalControls>
            <ModalImageContainer
              onClick={e => e.stopPropagation()}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onWheel={handleWheel}
            >
              <img
                src={paymentMethod === 'netbanking' ? nbPreviewUrl || '' : ddPreviewUrl || ''}
                alt={paymentMethod === 'netbanking' ? 'NetBanking Screenshot' : 'DD Full View'}
                style={{
                  transform: `translate(${imagePan.x}px, ${imagePan.y}px) scale(${imageZoom}) rotate(${imageRotation}deg)`,
                  transition: isDragging ? 'none' : 'transform 0.2s',
                  maxWidth: '85vw',
                  maxHeight: '65vh',
                  userSelect: 'none',
                  pointerEvents: 'none'
                }}
                draggable={false}
              />
            </ModalImageContainer>
            <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', marginTop: '8px' }}>
              Scroll to zoom • Drag to pan • Use buttons for rotation
            </div>
          </ImageModal>
        )}
        
        <GoBackButton onClick={() => navigate(-1)} disabled={loading}>
          ← Go Back
        </GoBackButton>
      </PaymentContainer>
    </PaymentPageWrapper>
  );
};

export default PaymentDetailsPage;